import { useEffect, useState } from "react";
import { Box, Dialog, IconButton, Typography, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { apiGetRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";

// Grow's own business-details form, hosted in a window of ours. Everything
// inside the frame belongs to Grow: the fields, the validation, the submit, and
// the "thank you, you can close this" screen at the end. Nothing is posted back
// to us. Grow only tells the backend once the business is actually created,
// which can be days later — nothing arrives when a form is merely submitted.
//
// So this modal owes the supplier exactly two things: the form, and a way out
// of it. Closing tells us nothing by itself — whoever opened it then asks the
// supplier whether the form was completed.

const KycDetailsModal = ({ open, onClose }) => {
  const { t } = useTranslation("supplier-kyc");
  const [formUrl, setFormUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setFailed(false);
    setFormUrl(null);

    apiGetRequest(endpoints.getKycOnboardingLink)
      .then((response) => {
        const url = response?.data?.url;
        if (!active) return;
        if (url) setFormUrl(url);
        else setFailed(true);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      // Grow's form is a one-shot: a lead can't be reissued. So only the X
      // closes it — a stray click outside shouldn't end the process.
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullWidth
      // Grow's form is a narrow column of fields — a wide window would just be
      // empty space either side of it.
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: "12px", width: "420px", height: "min(600px, 85vh)" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "12px 16px",
          borderBottom: "1px solid rgba(234, 234, 234, 1)",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
          {t("details.modal_title")}
        </Typography>
        <IconButton onClick={onClose} aria-label={t("details.close")}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        {formUrl ? (
          <Box
            component="iframe"
            src={formUrl}
            title={t("details.modal_title")}
            sx={{ width: "100%", height: "100%", border: 0, display: "block" }}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            {failed ? (
              <Typography sx={{ fontSize: "14px", color: "rgba(62, 63, 76, 0.8)" }}>
                {t("details.load_failed")}
              </Typography>
            ) : (
              <CircularProgress size={28} />
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default KycDetailsModal;
